import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Table, Tag, Switch, Space, Modal, Typography, Spin, message, Tooltip, Tree } from "antd";
import { ReloadOutlined, CheckOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
  getTaxonomySettings,
  updateTaxonomySettings,
  getTaxonomyJobs,
  getTaxonomyTopics,
  triggerTaxonomyGeneration,
  approveTaxonomyJob,
} from "../../../services/polly/crawlSourceService";

const { Text, Title } = Typography;

const statusColors = {
  queued: "default",
  running: "processing",
  awaiting_review: "warning",
  approved: "success",
  failed: "error",
};

const TaxonomyManagement = () => {
  const [settings, setSettings] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [settingsData, jobsData, topicsData] = await Promise.all([
        getTaxonomySettings().catch(() => null),
        getTaxonomyJobs().catch(() => []),
        getTaxonomyTopics().catch(() => []),
      ]);
      setSettings(settingsData);
      setJobs(jobsData || []);
      setTopics(topicsData || []);
    } catch (err) {
      console.error("Failed to load taxonomy data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAutoGenerateToggle = async (checked) => {
    try {
      const updated = await updateTaxonomySettings({ autoGenerateTaxonomyAfterCrawl: checked });
      setSettings(updated);
      message.success(checked ? "Auto-generation enabled" : "Auto-generation disabled");
    } catch (err) {
      message.error("Failed to update setting");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await triggerTaxonomyGeneration();
      message.success("Topic generation triggered");
      setTimeout(loadData, 3000);
    } catch (err) {
      message.error("Failed to trigger generation");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (jobId) => {
    setApproving(jobId);
    try {
      await approveTaxonomyJob(jobId, { reviewedBy: "admin" });
      message.success("Taxonomy approved and activated");
      loadData();
    } catch (err) {
      message.error("Failed to approve: " + (err.response?.data?.error || err.message));
    } finally {
      setApproving(null);
    }
  };

  const buildTopicTree = (topics) => {
    const byId = {};
    const roots = [];
    topics.forEach(t => { byId[t.id] = { ...t, children: [] }; });
    topics.forEach(t => {
      if (t.parentTopic && byId[t.parentTopic.id]) {
        byId[t.parentTopic.id].children.push(byId[t.id]);
      } else if (t.depth === 0) {
        roots.push(byId[t.id]);
      }
    });

    const toTreeData = (nodes) => nodes.map(n => ({
      title: (
        <span>
          {n.displayName}
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            {n.canonicalId} · {n.usageCount} uses
          </Text>
        </span>
      ),
      key: n.id,
      children: n.children.length > 0 ? toTreeData(n.children) : undefined,
    }));
    return toTreeData(roots);
  };

  const jobColumns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColors[status] || "default"}>{status}</Tag>,
    },
    {
      title: "Trigger",
      dataIndex: "triggerType",
      key: "triggerType",
      render: (t) => t === "manual" ? "Manual" : "Post-crawl",
    },
    {
      title: "Chunks",
      dataIndex: "chunksSelected",
      key: "chunksSelected",
    },
    {
      title: "Topics",
      key: "topicCount",
      render: (_, record) => record.proposal?.items?.length || "—",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => d ? new Date(d).toLocaleString() : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.status === "awaiting_review" && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={approving === record.id}
              onClick={() => handleApprove(record.id)}
            >
              Approve
            </Button>
          )}
          {record.proposal?.items?.length > 0 && (
            <Button size="small" onClick={() => setSelectedJob(record)}>
              View Proposal
            </Button>
          )}
          {record.status === "running" && (
            <Tag icon={<ClockCircleOutlined spin />} color="processing">Running</Tag>
          )}
        </Space>
      ),
    },
  ];

  const proposalColumns = [
    { title: "Topic", dataIndex: "displayName", key: "displayName" },
    { title: "ID", dataIndex: "canonicalId", key: "canonicalId", render: (v) => <Text code>{v}</Text> },
    { title: "Parent", dataIndex: "parentCanonicalId", key: "parentCanonicalId", render: (v) => v || "—" },
    { title: "Depth", dataIndex: "depth", key: "depth" },
    { title: "Action", dataIndex: "action", key: "action", render: (v) => <Tag>{v}</Tag> },
    {
      title: "Status", dataIndex: "itemStatus", key: "itemStatus",
      render: (v) => <Tag color={v === "approved" ? "success" : "default"}>{v}</Tag>,
    },
  ];

  if (loading) return <Spin size="large" style={{ display: "block", margin: "40px auto" }} />;

  return (
    <div style={{ maxWidth: 1000 }}>
      <Card title="Topic Generation Settings" style={{ marginBottom: 16 }}>
        {settings ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Text strong>Auto-generate topics after crawl</Text>
                <br />
                <Text type="secondary">When enabled, topics are automatically generated after each crawl that discovers new content</Text>
              </div>
              <Switch
                checked={settings.autoGenerateTaxonomyAfterCrawl}
                onChange={handleAutoGenerateToggle}
              />
            </div>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={generating}
              onClick={handleGenerate}
            >
              Generate Topics Now
            </Button>
          </Space>
        ) : (
          <Text type="secondary">No classification settings configured for this client</Text>
        )}
      </Card>

      <Card title="Generation Jobs" style={{ marginBottom: 16 }}>
        <Table
          dataSource={jobs}
          columns={jobColumns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 5 }}
        />
      </Card>

      <Card title={`Active Topics (${topics.length})`}>
        {topics.length > 0 ? (
          <Tree
            treeData={buildTopicTree(topics)}
            defaultExpandAll
            selectable={false}
          />
        ) : (
          <Text type="secondary">No active topics. Generate or approve a taxonomy to create topics.</Text>
        )}
      </Card>

      <Modal
        title={`Proposal — Job #${selectedJob?.id}`}
        open={!!selectedJob}
        onCancel={() => setSelectedJob(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedJob(null)}>Close</Button>,
          selectedJob?.status === "awaiting_review" && (
            <Button
              key="approve"
              type="primary"
              loading={approving === selectedJob?.id}
              onClick={() => { handleApprove(selectedJob.id); setSelectedJob(null); }}
            >
              Approve & Activate
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedJob?.proposal?.items && (
          <Table
            dataSource={selectedJob.proposal.items}
            columns={proposalColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </Modal>
    </div>
  );
};

export default TaxonomyManagement;
